import React, { useEffect, useState } from "react";
import CopyField from "@/components/CopyField";
import FeedUrlPanel from "@/components/FeedUrlPanel";
import PortalPublishAssistant from "@/components/PortalPublishAssistant";
import DismissibleIntro from "@/components/DismissibleIntro";
import SectionCoachmark from "@/components/SectionCoachmark";
import { formatPrice } from "@/components/propertyDetail/helpers";
import {
  Sparkle, Spinner, ArrowSquareOut, Archive, Broadcast,
  CheckCircle, Rocket, X, Clock, Lightning, Browsers, Copy,
} from "@phosphor-icons/react";
import { usePropertyDetail } from "../context";
import {
  CHROME_STORE_URL,
  EXTENSION_LIVE,
  EXTENSION_INSTALLED_LS_KEY,
} from "@/lib/extensionConstants";
import { isNative } from "@/lib/native";

/**
 * PortaliTab · iter 311 — versione "essenziale"
 * ─────────────────────────────────────────────
 * L'agente si lamentava che era "una giungla". Riscritto a colonna unica,
 * 3 blocchi soli + 1 collassabile per tutto il resto.
 *
 * Struttura nuova:
 *   1. Estensione Chrome (protagonista — la soluzione vera)
 *   2. Pubblicazione guidata (4 bottoni portali + 4 testi pronti)
 *   3. <details> "Altri strumenti" (ZIP, campi copia-manuale, APE, Feed XML)
 *
 * Rimosso:
 *   - Sidebar "Quale strada per te" + "Apri il portale a mano"
 *   - Box "Apri ricerche filtrate"
 *   - APE come card centrale (ora dentro il collassabile)
 *   - PortalCompare (spostato dentro il collassabile)
 *
 * Fix:
 *   - insert_url puntano a homepage portale (no più "sito irraggiungibile")
 *   - Assistente: istruzioni più chiare ("accedi e vai a Nuovo annuncio")
 */
export default function PortaliTab() {
  const {
    id, property, setProperty, photos, portalLinks, pack, packLoading,
    onGeneratePortalPack, onDownloadZip,
  } = usePropertyDetail();

  const realPhotos = photos.filter((p) => (p.photo_kind || "photo") === "photo" && !p.is_deleted);
  const photoCount = realPhotos.length;

  const [assistantPortal, setAssistantPortal] = useState(null);
  const startPublish = (key) => {
    const info = portalLinks?.[key];
    if (!info) return;
    window.open(info.insert_url, "_blank", "noopener");
    setAssistantPortal({ key, name: info.name, url: info.insert_url });
  };

  const PORTALS = [
    { key: "immobiliare", color: "#0033A0" },
    { key: "idealista",   color: "#FF6E00" },
  ];

  return (
    <div className="mt-8 max-w-3xl mx-auto">

      {/* iter v33 r9 · Coachmark uniforme "Cosa puoi fare qui" — sostituisce
          sia il vecchio DismissibleIntro sia il PortaliAutopilotCoachmark
          modale (più invasivo). Un solo tutorial inline, dismissibile.
          iter r96b · su iOS/Android l'estensione Chrome non è disponibile → 
          rimuoviamo lo step "Estensione Chrome" dal coachmark. */}
      <SectionCoachmark
        id="property-portali"
        title={isNative ? "Pubblica sui portali in due mosse" : "Pubblica sui portali in tre mosse"}
        steps={isNative ? [
          {
            icon: <ArrowSquareOut size={15} weight="bold" />,
            title: "Apri Immobiliare o Idealista",
            body: "Sull'app iPhone: apri il portale e usa la pubblicazione guidata qui sotto.",
          },
          {
            icon: <Copy size={15} weight="bold" />,
            title: "Copia & Incolla",
            body: "Titolo, descrizione, features, prezzo: tutto già ottimizzato per ciascun portale.",
          },
        ] : [
          {
            icon: <Browsers size={15} weight="bold" />,
            title: "Estensione Chrome (autopilot)",
            body: "Installi Pronto Publisher e l'estensione compila quasi tutti i campi al posto tuo.",
          },
          {
            icon: <ArrowSquareOut size={15} weight="bold" />,
            title: "Oppure pubblica a mano",
            body: "Apri Immobiliare o Idealista. Pronto ti porge ogni campo pronto da copiare.",
          },
          {
            icon: <Copy size={15} weight="bold" />,
            title: "Copia & Incolla",
            body: "Titolo, descrizione, features, prezzo: tutto già ottimizzato per ciascun portale.",
          },
        ]}
      />

      {/* ── 1️⃣ ESTENSIONE CHROME · PROTAGONISTA ──────────────────── */}
      <ExtensionPortaliCallout />

      {/* ── 2️⃣ PUBBLICAZIONE GUIDATA ─────────────────────────────── */}
      <div className="mb-6 card-flat p-6 sm:p-8" data-testid="pubblica-guidato-block">
        {/* iter v28 r177 — la spiegazione header è dismissibile, i bottoni
            portali e i 4 testi restano sempre visibili sotto. */}
        <DismissibleIntro storageKey="portali:pubblica-guidata-intro" align="left" className="mb-4">
          <div className="flex items-start gap-3 pr-9">
            <div className="hidden sm:flex w-10 h-10 rounded-full bg-[#1C1C1A] text-white items-center justify-center flex-shrink-0">
              <Rocket size={18} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#1C1C1A] mb-1">
                Pubblicazione guidata · ~30 secondi a portale
              </div>
              <h3 className="font-editorial text-xl sm:text-2xl leading-tight mb-2">
                Scegli un portale · Pronto ti porge ogni campo pronto da incollare
              </h3>
              <p className="text-[13px] text-[#6B6A65] leading-relaxed">
                Clicca un portale. Apriamo la sua pagina in un'altra scheda — tu accedi
                e vai su <strong>"Nuovo annuncio"</strong>. Qui in basso a destra compare un
                pannello con i tuoi campi numerati: <strong>Copia & Avanti</strong>, alterni
                scheda, incolli. Stop.
              </p>
            </div>
          </div>
        </DismissibleIntro>

        {/* Step 1 inline: prepara i 4 testi (solo se non già fatto) */}
        {!pack ? (
          <div className="mb-4 p-4 rounded-md bg-[#FAF9F4] border border-[#EAE7DF] flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold text-[#1C1C1A]">
                Prima · scrivo 2 testi diversi (uno per portale)
              </div>
              <div className="text-[11.5px] text-[#6B6A65] mt-0.5 leading-snug">
                Immobiliare e Idealista premiano testi diversi. 1 credito.
              </div>
            </div>
            <button
              onClick={onGeneratePortalPack}
              disabled={packLoading}
              className="inline-flex items-center gap-2 bg-[#1C1C1A] hover:bg-black text-white font-semibold text-[12.5px] px-3.5 py-2 rounded-md disabled:opacity-60 transition-colors"
              data-testid="portal-pack-generate-btn"
            >
              {packLoading
                ? <><Spinner size={13} className="animate-spin" /> Preparo…</>
                : <><Sparkle size={13} weight="fill" /> Preparami i 2 testi</>}
            </button>
          </div>
        ) : (
          <div className="mb-4 px-4 py-2.5 rounded-md bg-[#F1F4ED] border border-[#7C8B6E]/30 flex items-center justify-between gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[#5d6952]">
              <CheckCircle size={14} weight="fill" /> Testi pronti per ogni portale
            </div>
            <button
              onClick={onGeneratePortalPack}
              disabled={packLoading}
              className="text-[11.5px] text-[#7C8B6E] hover:text-[#5d6952] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="portal-pack-regen-btn"
            >
              {packLoading ? <Spinner size={11} className="animate-spin" /> : <Sparkle size={11} weight="fill" />}
              Rigenera
            </button>
          </div>
        )}

        {/* Step 2: i 4 bottoni portali */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {PORTALS.map(({ key, color }) => portalLinks?.[key] && (
            <button
              key={key}
              onClick={() => startPublish(key)}
              className="group flex items-center justify-between gap-3 px-4 py-3.5 rounded-md border-2 border-[#1C1C1A] bg-[#1C1C1A] hover:bg-black text-white font-semibold text-[13px] transition-all"
              data-testid={`publish-assistant-${key}`}
              title={`Apri ${portalLinks[key].name} e avvia la guida campo per campo`}
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <span className="w-1.5 h-6 rounded-full shrink-0" style={{ background: color }} />
                <span className="truncate text-left">
                  <span className="block text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold">Pubblica guidato</span>
                  <span className="block text-[14px]">{portalLinks[key].name}</span>
                </span>
              </span>
              <ArrowSquareOut size={14} weight="bold" className="shrink-0 text-[#A8B89A] group-hover:translate-x-0.5 transition-transform" />
            </button>
          ))}
        </div>

        {/* Testi pronti — preview inline (espandibile) */}
        {pack && (
          <details className="mt-5 pt-4 border-t border-[#EAE7DF] group" data-testid="portal-pack-fields">
            <summary className="cursor-pointer list-none flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#9A968B]">
                Vedi i 2 testi (per copia rapida fuori dall'assistente)
              </span>
              <span className="text-[#6B6A65] text-xs group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="mt-3 space-y-1">
              <CopyField label="Idealista" value={pack.idealista} testId="copy-pack-idealista" multiline />
              <CopyField label="Immobiliare.it" value={pack.immobiliare} testId="copy-pack-immobiliare" multiline />
              <CopyField label="Email al cliente" value={pack.email} testId="copy-pack-email" multiline />
              <CopyField label="Messaggio WhatsApp" value={pack.whatsapp} testId="copy-pack-whatsapp" multiline />
              {Array.isArray(pack.tags) && pack.tags.length > 0 && (
                <CopyField label="Tag SEO" value={pack.tags.join(" · ")} testId="copy-pack-tags" />
              )}
            </div>
          </details>
        )}
      </div>

      {/* ── 3️⃣ ALTRI STRUMENTI · TUTTO IL RESTO COLLASSATO ───────── */}
      <details className="rounded-xl border border-[#E5E7E0] bg-[#FAFAF7] group mb-6" data-testid="altri-strumenti">
        <summary className="cursor-pointer list-none p-5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6B6A65] mb-1">
              Altri strumenti
            </div>
            <h4 className="font-editorial text-base leading-tight text-[#1C1C1A]">
              Feed XML · ZIP foto · campi da copiare
            </h4>
          </div>
          <span className="text-[#6B6A65] text-xs group-open:rotate-180 transition-transform shrink-0">▾</span>
        </summary>

        <div className="px-5 pb-6 -mt-1 border-t border-[#EAE7DF] space-y-6 pt-5">
          {/* iter 312 r8 — Feed XML PRIMA (è la cosa più importante per Pro
              che hanno 10+ annunci), poi ZIP foto, poi campi manuali.
              Rimossa APE: si carica già dai Documenti/Foto della scheda. */}
          {/* Feed XML — riservato Pro */}
          <details className="rounded-md border border-[#EAE7DF] bg-white group/feed" data-testid="feed-xml-collapsible" open>
            <summary className="cursor-pointer list-none p-4 flex items-start gap-3">
              <Broadcast size={18} weight="fill" className="text-[#7C8B6E] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-0.5">
                  Solo agenzie Pro · 10+ annunci/mese
                </div>
                <div className="font-editorial text-[15px] leading-tight text-[#1C1C1A]">
                  Feed XML automatico
                </div>
                <p className="text-[11.5px] text-[#6B6A65] mt-1 leading-snug">
                  Pronto manda gli annunci al portale da solo. Si configura una volta.
                </p>
              </div>
              <span className="text-[#6B6A65] text-xs group-open/feed:rotate-180 transition-transform shrink-0 mt-2">▾</span>
            </summary>
            <div className="px-4 pb-4 -mt-1 border-t border-[#EAE7DF] pt-4">
              <div className="mb-4 p-3 bg-[#F1F4ED] border-l-4 border-[#7C8B6E] rounded-r-md">
                <p className="text-[12.5px] text-[#1C1C1A] leading-relaxed">
                  Il <strong>Feed XML</strong> è come dare al portale la <strong>chiave di casa</strong>:
                  ogni volta che modifichi un annuncio su Pronto, lui se ne accorge
                  da solo e lo aggiorna sul portale.
                </p>
              </div>
              <FeedUrlPanel />
            </div>
          </details>

          {/* ZIP foto */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">
              Scarica ZIP foto
            </div>
            <button
              onClick={() => onDownloadZip("all")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#F1F4ED] hover:bg-[#E5E7E0] text-[#1C1C1A] font-semibold text-[13px] px-4 py-2.5 rounded-md transition-colors disabled:opacity-50"
              disabled={photoCount === 0}
              data-testid="download-zip-btn"
              title={photoCount === 0 ? "Carica prima delle foto" : `Scarica ${photoCount} foto in un ZIP`}
            >
              <Archive size={14} weight="bold" />
              Scarica ZIP {photoCount > 0 && <span className="opacity-70">· {photoCount} foto</span>}
            </button>
          </div>

          {/* Campi base — per copia manuale (solo quelli con valore) */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">
              Tutti i campi · copia manuale
            </div>
            {property?.title && <CopyField label="Titolo" value={property.title} testId="copy-title" />}
            {property?.property_type && <CopyField label="Tipologia" value={property.property_type} testId="copy-type" />}
            {property?.price && <CopyField label="Prezzo" value={formatPrice(property.price)} testId="copy-price" />}
            {property?.surface && <CopyField label="Superficie" value={`${property.surface} m²`} testId="copy-surface" />}
            {property?.rooms && <CopyField label="Locali" value={String(property.rooms)} testId="copy-rooms" />}
            {property?.bathrooms && <CopyField label="Bagni" value={String(property.bathrooms)} testId="copy-bathrooms" />}
            {property?.floor && <CopyField label="Piano" value={String(property.floor)} testId="copy-floor" />}
            {(property?.address || property?.city) && (
              <CopyField label="Indirizzo" value={[property?.address, property?.city].filter(Boolean).join(", ")} testId="copy-address" />
            )}
            {Array.isArray(property?.features) && property.features.length > 0 && (
              <CopyField label="Caratteristiche" value={property.features.join(", ")} testId="copy-features" />
            )}
            {property?.energy_class && <CopyField label="Classe energetica" value={property.energy_class} testId="copy-energy" />}
            {property?.ai_description && (
              <CopyField label="Descrizione completa" value={property.ai_description} testId="copy-description" multiline />
            )}
          </div>
        </div>
      </details>

      {/* ── Assistente di pubblicazione · pannello flottante ─────────── */}
      <PortalPublishAssistant
        open={!!assistantPortal}
        onClose={() => setAssistantPortal(null)}
        portalKey={assistantPortal?.key}
        portalName={assistantPortal?.name}
        portalUrl={assistantPortal?.url}
        property={property}
        pack={pack}
        onDownloadZip={() => onDownloadZip("all")}
        photoCount={photoCount}
      />
    </div>
  );
}

/**
 * ExtensionPortaliCallout — Box "Pronto Publisher" in cima al tab.
 * Auto-hide se l'utente ha già installato l'estensione (flag localStorage).
 */
function ExtensionPortaliCallout() {
  const [hidden, setHidden] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(EXTENSION_INSTALLED_LS_KEY) === "1") {
        setHidden(true);
        return;
      }
      setHidden(false);
    } catch {
      setHidden(false);
    }
  }, []);

  if (hidden || dismissed) return null;

  // iter r96b — Su app iOS/Android l'estensione Chrome è impossibile da
  // installare (Apple non ammette WebExtensions su Safari mobile). Mostriamo
  // un callout esplicativo che rinvia al desktop invece di far scaricare
  // un file che non funzionerà mai.
  if (isNative) {
    return (
      <div
        className="relative mb-6 p-6 rounded-xl border-2 border-[#D5DAC9] bg-gradient-to-br from-[#F1F4ED] via-white to-[#F1F4ED] pr-12"
        data-testid="portali-ext-callout-mobile"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-[#D5DAC9] flex-shrink-0 flex items-center justify-center">
            <Browsers size={18} weight="bold" className="text-[#5d6952]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5d6952] mb-1.5">
              Disponibile solo da computer
            </div>
            <h3 className="font-editorial text-xl sm:text-[22px] leading-tight mb-2 text-[#1C1C1A]">
              Pronto Publisher · da desktop
            </h3>
            <p className="text-[13px] text-[#6B6A65] leading-relaxed max-w-2xl">
              L&apos;estensione Chrome che pubblica sui portali con 1 click funziona solo su computer.
              Apri <strong>prontoimmo.it</strong> dal browser del tuo desktop (Chrome o Edge) per installarla.
              Da iPhone puoi usare la pubblicazione guidata qui sotto.
            </p>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#9A968B] hover:text-[#1C1C1A] hover:bg-white/60 inline-flex items-center justify-center transition-colors"
          aria-label="Chiudi"
          data-testid="portali-ext-callout-mobile-close"
        >
          <X size={13} weight="bold" />
        </button>
      </div>
    );
  }

  if (!EXTENSION_LIVE) {
    return (
      <div
        className="relative mb-6 p-6 rounded-xl border-2 border-[#F2D89E] bg-gradient-to-br from-[#FFF6E5] via-white to-[#FFF6E5] pr-12"
        data-testid="portali-ext-callout-waitlist"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-[#F2D89E] flex-shrink-0 flex items-center justify-center">
            <Clock size={18} weight="bold" className="text-[#8B6914]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#8B6914] mb-1.5">
              In arrivo · la soluzione definitiva
            </div>
            <h3 className="font-editorial text-xl sm:text-[22px] leading-tight mb-2 text-[#1C1C1A]">
              Pronto Publisher · pubblica con 1 click
            </h3>
            <p className="text-[13px] text-[#6B6A65] leading-relaxed max-w-2xl mb-3">
              Estensione Chrome in revisione su Google Web Store. Quando esce, compila
              <strong> tutti i campi su Immobiliare.it e Idealista</strong> al
              posto tuo. Mentre aspetti, usa la pubblicazione guidata qui sotto.
            </p>
            <a
              href="/estensione"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#8B6914] hover:text-[#5d4708] underline underline-offset-2"
              data-testid="portali-ext-callout-info"
            >
              Scopri come funziona →
            </a>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#9A968B] hover:text-[#1C1C1A] hover:bg-white/60 inline-flex items-center justify-center transition-colors"
          aria-label="Chiudi"
          data-testid="portali-ext-callout-close"
        >
          <X size={13} weight="bold" />
        </button>
      </div>
    );
  }

  // EXTENSION_LIVE = true
  return (
    <div
      className="relative mb-6 p-6 rounded-xl border-2 border-[#7C8B6E] bg-gradient-to-br from-[#F1F4ED] via-white to-[#F1F4ED] pr-12"
      data-testid="portali-ext-callout-live"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-[#7C8B6E] flex-shrink-0 flex items-center justify-center">
          <Lightning size={18} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-1.5">
            La soluzione definitiva è qui
          </div>
          <h3 className="font-editorial text-xl sm:text-[22px] leading-tight mb-2 text-[#1C1C1A]">
            Pronto Publisher · pubblica con 1 click
          </h3>
          <p className="text-[13px] text-[#6B6A65] leading-relaxed max-w-2xl">
            L'estensione Chrome compila tutti i campi su Immobiliare.it e Idealista al posto tuo.
            Installazione in 10 secondi.
          </p>
          <a
            href={CHROME_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-[#7C8B6E] hover:bg-[#5d6952] text-white text-[13px] font-semibold transition-colors mt-3"
            data-testid="portali-ext-callout-install"
          >
            Aggiungi a Chrome <ArrowSquareOut size={13} weight="bold" />
          </a>
        </div>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#9A968B] hover:text-[#1C1C1A] hover:bg-white/60 inline-flex items-center justify-center transition-colors"
        aria-label="Chiudi"
        data-testid="portali-ext-callout-close"
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}
